import React, { useEffect, useState } from "react";

const Documentation = () => {
	const [activeSection, setActiveSection] = useState('getting-started');

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const documentationSections = [
		{
			id: 'getting-started',
			title: 'Getting Started',
			icon: '🚀'
		},
		{
			id: 'business-verification',
			title: 'Business Verification',
			icon: '✅'
		},
		{
			id: 'browsing-products',
			title: 'Browsing Products',
			icon: '🔍'
		},
		{
			id: 'placing-orders',
			title: 'Placing Orders',
			icon: '📦'
		},
		{
			id: 'managing-account',
			title: 'Managing Your Account',
			icon: '👤'
		},
		{
			id: 'payments-billing',
			title: 'Payments & Billing',
			icon: '💳'
		},
		{
			id: 'support',
			title: 'Support & FAQ',
			icon: '❓'
		}
	];

	const scrollToSection = (sectionId) => {
		setActiveSection(sectionId);
		const element = document.getElementById(sectionId);
		if (element) {
			element.scrollIntoView({ behavior: 'smooth' });
		}
	};

	return (
		<div className="bg-gray-50 min-h-screen">
			{/* Header */}
			<div className="flex justify-center py-10 lg:py-20 bg-page-header-bg bg-brand-100 w-full bg-cover bg-no-repeat bg-bottom bg-page-header">
				<div className="flex mx-auto w-full max-w-screen-2xl px-3 sm:px-10">
					<div className="w-full flex justify-center flex-col relative">
						<h2 className="text-xl md:text-3xl lg:text-4xl font-bold text-center text-black">
							User Documentation
						</h2>
						<p className="text-center mt-4 text-gray-600">
							Complete guide to using WholesaleDai.com
						</p>
					</div>
				</div>
			</div>

			<div className="max-w-screen-2xl mx-auto lg:py-20 py-10 px-4 sm:px-10">
				<div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
					{/* Sidebar Navigation */}
					<div className="lg:col-span-1">
						<div className="bg-white rounded-lg shadow-sm p-6 sticky top-6">
							<h3 className="text-lg font-semibold mb-4">Table of Contents</h3>
							<nav className="space-y-2">
								{documentationSections.map((section) => (
									<button
										key={section.id}
										onClick={() => scrollToSection(section.id)}
										className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
											activeSection === section.id
												? 'bg-brand-100 text-brand-700 font-medium'
												: 'text-gray-700 hover:bg-gray-100'
										}`}
									>
										<span className="mr-2">{section.icon}</span>
										{section.title}
									</button>
								))}
							</nav>
						</div>
					</div>

					{/* Main Content */}
					<div className="lg:col-span-3">
						<div className="bg-white rounded-lg shadow-sm p-8">
							{/* Getting Started */}
							<section id="getting-started" className="mb-12">
								<h2 className="text-2xl font-bold mb-6 flex items-center">
									<span className="mr-3">🚀</span>Getting Started
								</h2>
								<div className="prose max-w-none">
									<h3 className="text-xl font-semibold mb-4">Welcome to WholesaleDai.com</h3>
									<p className="mb-4">
										WholesaleDai.com is Nepal's leading B2B marketplace designed to connect verified businesses, 
										wholesalers, and suppliers for seamless bulk trade. This guide will help you get started and 
										make the most of our platform.
									</p>
									
									<h4 className="text-lg font-semibold mb-3">What You Need to Get Started:</h4>
									<ul className="list-disc pl-6 mb-6 space-y-2">
										<li>Valid business registration documents</li>
										<li>Business contact information</li>
										<li>Valid email address and phone number</li>
										<li>Business bank account details (for transactions)</li>
									</ul>

									<div className="bg-brand-50 p-4 rounded-lg mb-6">
										<h4 className="font-semibold text-brand-800 mb-2">Quick Start Steps:</h4>
										<ol className="list-decimal pl-6 space-y-1 text-brand-700">
											<li>Register your business account</li>
											<li>Submit verification documents</li>
											<li>Wait for approval (typically 24-48 hours)</li>
											<li>Start browsing and placing orders</li>
										</ol>
									</div>
								</div>
							</section>

							{/* Business Verification */}
							<section id="business-verification" className="mb-12">
								<h2 className="text-2xl font-bold mb-6 flex items-center">
									<span className="mr-3">✅</span>Business Verification
								</h2>
								<div className="prose max-w-none">
									<p className="mb-4">
										WholesaleDai maintains strict verification standards to ensure a trustworthy trading environment. 
										Only legitimate businesses with verified documents can access our marketplace.
									</p>

									<h3 className="text-xl font-semibold mb-4">Required Documents:</h3>
									<div className="grid md:grid-cols-2 gap-4 mb-6">
										<div className="bg-gray-50 p-4 rounded-lg">
											<h4 className="font-semibold mb-2">For Private Companies:</h4>
											<ul className="text-sm space-y-1">
												<li>• Company registration certificate</li>
												<li>• VAT/PAN registration</li>
												<li>• Bank account verification letter</li>
												<li>• Director's citizenship copies</li>
											</ul>
										</div>
										<div className="bg-gray-50 p-4 rounded-lg">
											<h4 className="font-semibold mb-2">For Sole Proprietorship:</h4>
											<ul className="text-sm space-y-1">
												<li>• Business registration certificate</li>
												<li>• Owner's citizenship copy</li>
												<li>• VAT/PAN certificate</li>
												<li>• Bank account details</li>
											</ul>
										</div>
									</div>

									<h3 className="text-xl font-semibold mb-4">Verification Process:</h3>
									<div className="space-y-4">
										<div className="flex items-start">
											<div className="w-8 h-8 bg-brand-100 text-brand-700 rounded-full flex items-center justify-center mr-4 mt-1">1</div>
											<div>
												<h4 className="font-semibold">Submit Application</h4>
												<p className="text-gray-600">Complete the registration form with accurate business information.</p>
											</div>
										</div>
										<div className="flex items-start">
											<div className="w-8 h-8 bg-brand-100 text-brand-700 rounded-full flex items-center justify-center mr-4 mt-1">2</div>
											<div>
												<h4 className="font-semibold">Document Upload</h4>
												<p className="text-gray-600">Upload clear photos or scans of all required documents.</p>
											</div>
										</div>
										<div className="flex items-start">
											<div className="w-8 h-8 bg-brand-100 text-brand-700 rounded-full flex items-center justify-center mr-4 mt-1">3</div>
											<div>
												<h4 className="font-semibold">Review Process</h4>
												<p className="text-gray-600">Our team reviews your application within 24-48 hours.</p>
											</div>
										</div>
										<div className="flex items-start">
											<div className="w-8 h-8 bg-brand-100 text-brand-700 rounded-full flex items-center justify-center mr-4 mt-1">4</div>
											<div>
												<h4 className="font-semibold">Approval Notification</h4>
												<p className="text-gray-600">Receive email notification once your account is approved.</p>
											</div>
										</div>
									</div>
								</div>
							</section>

							{/* Browsing Products */}
							<section id="browsing-products" className="mb-12">
								<h2 className="text-2xl font-bold mb-6 flex items-center">
									<span className="mr-3">🔍</span>Browsing Products
								</h2>
								<div className="prose max-w-none">
									<h3 className="text-xl font-semibold mb-4">Finding Products:</h3>
									<ul className="list-disc pl-6 mb-6 space-y-2">
										<li><strong>Search Bar:</strong> Use keywords to find specific products</li>
										<li><strong>Categories:</strong> Browse by product categories</li>
										<li><strong>Filters:</strong> Filter by price, quantity, location, and more</li>
										<li><strong>Supplier Directory:</strong> Browse by verified suppliers</li>
									</ul>

									<h3 className="text-xl font-semibold mb-4">Product Information:</h3>
									<p className="mb-4">Each product listing includes:</p>
									<div className="bg-gray-50 p-4 rounded-lg mb-6">
										<ul className="space-y-2">
											<li>• Product specifications and descriptions</li>
											<li>• Wholesale pricing tiers</li>
											<li>• Minimum order quantities (MOQ)</li>
											<li>• Supplier information and ratings</li>
											<li>• Delivery options and timelines</li>
											<li>• Product images and videos</li>
										</ul>
									</div>

									<h3 className="text-xl font-semibold mb-4">Using Filters Effectively:</h3>
									<div className="grid md:grid-cols-2 gap-4">
										<div>
											<h4 className="font-semibold mb-2">Price Filters:</h4>
											<ul className="text-sm space-y-1">
												<li>• Set price ranges</li>
												<li>• Compare wholesale rates</li>
												<li>• View bulk discounts</li>
											</ul>
										</div>
										<div>
											<h4 className="font-semibold mb-2">Location Filters:</h4>
											<ul className="text-sm space-y-1">
												<li>• Find local suppliers</li>
												<li>• Check delivery areas</li>
												<li>• Calculate shipping costs</li>
											</ul>
										</div>
									</div>
								</div>
							</section>

							{/* Placing Orders */}
							<section id="placing-orders" className="mb-12">
								<h2 className="text-2xl font-bold mb-6 flex items-center">
									<span className="mr-3">📦</span>Placing Orders
								</h2>
								<div className="prose max-w-none">
									<h3 className="text-xl font-semibold mb-4">Order Process:</h3>
									<div className="space-y-4 mb-6">
										<div className="flex items-start">
											<div className="w-8 h-8 bg-brand-100 text-brand-700 rounded-full flex items-center justify-center mr-4 mt-1">1</div>
											<div>
												<h4 className="font-semibold">Select Products</h4>
												<p className="text-gray-600">Add products to cart, specify quantities meeting MOQ requirements.</p>
											</div>
										</div>
										<div className="flex items-start">
											<div className="w-8 h-8 bg-brand-100 text-brand-700 rounded-full flex items-center justify-center mr-4 mt-1">2</div>
											<div>
												<h4 className="font-semibold">Review Cart</h4>
												<p className="text-gray-600">Check quantities, pricing, and delivery options.</p>
											</div>
										</div>
										<div className="flex items-start">
											<div className="w-8 h-8 bg-brand-100 text-brand-700 rounded-full flex items-center justify-center mr-4 mt-1">3</div>
											<div>
												<h4 className="font-semibold">Checkout</h4>
												<p className="text-gray-600">Provide delivery address and payment information.</p>
											</div>
										</div>
										<div className="flex items-start">
											<div className="w-8 h-8 bg-brand-100 text-brand-700 rounded-full flex items-center justify-center mr-4 mt-1">4</div>
											<div>
												<h4 className="font-semibold">Confirmation</h4>
												<p className="text-gray-600">Receive order confirmation and tracking information.</p>
											</div>
										</div>
									</div>

									<div className="bg-yellow-50 p-4 rounded-lg mb-6">
										<h4 className="font-semibold text-yellow-800 mb-2">Important Notes:</h4>
										<ul className="text-yellow-700 space-y-1">
											<li>• Always check minimum order quantities (MOQ)</li>
											<li>• Verify delivery timelines before placing orders</li>
											<li>• Review payment terms with suppliers</li>
											<li>• Keep order confirmations for your records</li>
										</ul>
									</div>

									<h3 className="text-xl font-semibold mb-4">Order Status Tracking:</h3>
									<div className="grid md:grid-cols-3 gap-4">
										<div className="text-center p-4 bg-brand-50 rounded-lg">
											<div className="w-12 h-12 bg-brand-500 text-white rounded-full flex items-center justify-center mx-auto mb-2">📝</div>
											<h4 className="font-semibold">Order Placed</h4>
											<p className="text-sm text-gray-600">Order submitted and confirmed</p>
										</div>
										<div className="text-center p-4 bg-yellow-50 rounded-lg">
											<div className="w-12 h-12 bg-yellow-500 text-white rounded-full flex items-center justify-center mx-auto mb-2">🏭</div>
											<h4 className="font-semibold">Processing</h4>
											<p className="text-sm text-gray-600">Supplier preparing your order</p>
										</div>
										<div className="text-center p-4 bg-brand-50 rounded-lg">
											<div className="w-12 h-12 bg-brand-500 text-white rounded-full flex items-center justify-center mx-auto mb-2">🚚</div>
											<h4 className="font-semibold">Delivered</h4>
											<p className="text-sm text-gray-600">Order delivered successfully</p>
										</div>
									</div>
								</div>
							</section>

							{/* Managing Account */}
							<section id="managing-account" className="mb-12">
								<h2 className="text-2xl font-bold mb-6 flex items-center">
									<span className="mr-3">👤</span>Managing Your Account
								</h2>
								<div className="prose max-w-none">
									<h3 className="text-xl font-semibold mb-4">Account Dashboard:</h3>
									<p className="mb-4">Your dashboard provides access to:</p>
									<div className="grid md:grid-cols-2 gap-4 mb-6">
										<div className="bg-gray-50 p-4 rounded-lg">
											<h4 className="font-semibold mb-2">Order Management:</h4>
											<ul className="text-sm space-y-1">
												<li>• View order history</li>
												<li>• Track current orders</li>
												<li>• Download invoices</li>
												<li>• Reorder previous items</li>
											</ul>
										</div>
										<div className="bg-gray-50 p-4 rounded-lg">
											<h4 className="font-semibold mb-2">Profile Settings:</h4>
											<ul className="text-sm space-y-1">
												<li>• Update business information</li>
												<li>• Change contact details</li>
												<li>• Manage delivery addresses</li>
												<li>• Update payment methods</li>
											</ul>
										</div>
									</div>

									<h3 className="text-xl font-semibold mb-4">Business Information Updates:</h3>
									<p className="mb-4">
										Keep your business information current to ensure smooth transactions:
									</p>
									<ul className="list-disc pl-6 mb-6 space-y-2">
										<li>Business name and registration details</li>
										<li>Contact person and authorization</li>
										<li>Delivery and billing addresses</li>
										<li>Payment preferences and terms</li>
									</ul>

									<div className="bg-red-50 p-4 rounded-lg">
										<h4 className="font-semibold text-red-800 mb-2">Security Tips:</h4>
										<ul className="text-red-700 space-y-1">
											<li>• Use strong, unique passwords</li>
											<li>• Enable two-factor authentication when available</li>
											<li>• Regularly review account activity</li>
											<li>• Never share login credentials</li>
										</ul>
									</div>
								</div>
							</section>

							{/* Payments & Billing */}
							<section id="payments-billing" className="mb-12">
								<h2 className="text-2xl font-bold mb-6 flex items-center">
									<span className="mr-3">💳</span>Payments & Billing
								</h2>
								<div className="prose max-w-none">
									<h3 className="text-xl font-semibold mb-4">Accepted Payment Methods:</h3>
									<div className="grid md:grid-cols-2 gap-4 mb-6">
										<div className="bg-brand-50 p-4 rounded-lg">
											<h4 className="font-semibold mb-2">Digital Payments:</h4>
											<ul className="text-sm space-y-1">
												<li>• Bank transfers</li>
												<li>• Digital wallets (eSewa, Khalti)</li>
												<li>• Online banking</li>
												<li>• Mobile banking</li>
											</ul>
										</div>
										<div className="bg-brand-50 p-4 rounded-lg">
											<h4 className="font-semibold mb-2">Business Terms:</h4>
											<ul className="text-sm space-y-1">
												<li>• Credit terms (approved businesses)</li>
												<li>• Advance payments</li>
												<li>• Letter of credit</li>
												<li>• Cash on delivery (COD)</li>
											</ul>
										</div>
									</div>

									<h3 className="text-xl font-semibold mb-4">Payment Process:</h3>
									<ol className="list-decimal pl-6 mb-6 space-y-2">
										<li>Select payment method during checkout</li>
										<li>Complete payment through chosen method</li>
										<li>Receive payment confirmation</li>
										<li>Supplier processes order after payment verification</li>
									</ol>

									<h3 className="text-xl font-semibold mb-4">Invoicing & Records:</h3>
									<ul className="list-disc pl-6 mb-6 space-y-2">
										<li>Digital invoices available for download</li>
										<li>VAT-compliant billing for registered businesses</li>
										<li>Monthly/quarterly statements available</li>
										<li>Transaction history accessible 24/7</li>
									</ul>
								</div>
							</section>

							{/* Support & FAQ */}
							<section id="support" className="mb-12">
								<h2 className="text-2xl font-bold mb-6 flex items-center">
									<span className="mr-3">❓</span>Support & FAQ
								</h2>
								<div className="prose max-w-none">
									<h3 className="text-xl font-semibold mb-4">Getting Help:</h3>
									<div className="grid md:grid-cols-3 gap-4 mb-6">
										<div className="text-center p-4 bg-brand-50 rounded-lg">
											<div className="w-12 h-12 bg-brand-500 text-white rounded-full flex items-center justify-center mx-auto mb-2">📧</div>
											<h4 className="font-semibold">Email Support</h4>
											<p className="text-sm text-gray-600">info@wholesaledai.com</p>
										</div>
										<div className="text-center p-4 bg-brand-50 rounded-lg">
											<div className="w-12 h-12 bg-brand-500 text-white rounded-full flex items-center justify-center mx-auto mb-2">📞</div>
											<h4 className="font-semibold">Phone Support</h4>
											<p className="text-sm text-gray-600">+977-1-5927066</p>
										</div>
										<div className="text-center p-4 bg-brand-50 rounded-lg">
											<div className="w-12 h-12 bg-brand-500 text-white rounded-full flex items-center justify-center mx-auto mb-2">💬</div>
											<h4 className="font-semibold">Live Chat</h4>
											<p className="text-sm text-gray-600">Available 9 AM - 6 PM</p>
										</div>
									</div>

									<h3 className="text-xl font-semibold mb-4">Common Questions:</h3>
									<div className="space-y-4">
										<div className="bg-gray-50 p-4 rounded-lg">
											<h4 className="font-semibold mb-2">How long does verification take?</h4>
											<p className="text-gray-700">Business verification typically takes 24-48 hours for complete applications with all required documents.</p>
										</div>
										<div className="bg-gray-50 p-4 rounded-lg">
											<h4 className="font-semibold mb-2">What are minimum order quantities?</h4>
											<p className="text-gray-700">MOQ varies by supplier and product. Check individual product pages for specific requirements.</p>
										</div>
										<div className="bg-gray-50 p-4 rounded-lg">
											<h4 className="font-semibold mb-2">Can I negotiate prices?</h4>
											<p className="text-gray-700">For large volume orders, you can contact suppliers directly to discuss custom pricing and terms.</p>
										</div>
										<div className="bg-gray-50 p-4 rounded-lg">
											<h4 className="font-semibold mb-2">How do I track my orders?</h4>
											<p className="text-gray-700">Use the order tracking feature in your dashboard or the tracking number provided via email.</p>
										</div>
									</div>

									<div className="bg-brand-50 p-6 rounded-lg mt-8">
										<h3 className="text-lg font-semibold text-brand-800 mb-3">Need Additional Help?</h3>
										<p className="text-brand-700 mb-4">
											Our support team is here to help you succeed on WholesaleDai.com. Don't hesitate to reach out with any questions or concerns.
										</p>
										<button className="bg-brand-600 text-white px-6 py-2 rounded-lg hover:bg-brand-700 transition">
											Contact Support
										</button>
									</div>
								</div>
							</section>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Documentation; 